export const PRIVATE_READ_TIMEOUT_MS = 20_000;
const READ_RETRIES = 2;
const READ_RETRY_BACKOFF_MS = [400, 1_200];
const RETRYABLE_STATUS = new Set([502, 503, 504]);
const delay = (ms: number) =>
  new Promise<void>((resolve) => setTimeout(resolve, ms));

export class PrivateReadHttpError extends Error {
  constructor(
    context: string,
    readonly status: number
  ) {
    super(`${context} (${status})`);
    this.name = "PrivateReadHttpError";
  }
}

export class PrivateReadTimeoutError extends Error {
  constructor() {
    super("Private account refresh timed out. Please try again.");
    this.name = "PrivateReadTimeoutError";
  }
}

/**
 * Bound both response headers and JSON consumption. Abort alone is insufficient
 * for wallet-injected transports that ignore its signal. This helper is strictly
 * read-only: a timeout is never evidence that a relayed transaction failed.
 */
export async function readPrivateJson(
  fetchImpl: typeof fetch,
  url: string,
  timeoutMs = PRIVATE_READ_TIMEOUT_MS,
  blockHeight?: bigint
): Promise<{ ok: boolean; status: number; body: unknown }> {
  if (!Number.isSafeInteger(timeoutMs) || timeoutMs <= 0) {
    throw new Error("Invalid private read timeout");
  }
  if (
    blockHeight !== undefined &&
    (blockHeight <= 0n || blockHeight > BigInt(Number.MAX_SAFE_INTEGER))
  )
    throw new Error("Invalid private read height");
  const controller = new AbortController();
  let timer: ReturnType<typeof setTimeout> | undefined;
  const expiry = new Promise<never>((_, reject) => {
    timer = setTimeout(() => {
      // Reject before aborting so an AbortError cannot obscure the useful cause.
      reject(new PrivateReadTimeoutError());
      controller.abort();
    }, timeoutMs);
  });
  try {
    return await Promise.race([
      expiry,
      (async () => {
        const request = () =>
          fetchImpl(url, {
            method: "GET",
            credentials: "omit",
            cache: "no-store",
            signal: controller.signal,
            ...(blockHeight !== undefined && {
              headers: { "x-cosmos-block-height": blockHeight.toString() },
            }),
          });
        // Reads are idempotent: retry upstream gateway errors (a flaky node
        // behind a load balancer) inside the same overall timeout budget.
        let response: Response | undefined;
        for (let attempt = 0; ; attempt += 1) {
          try {
            response = await request();
          } catch (error) {
            if (controller.signal.aborted || attempt >= READ_RETRIES)
              throw error;
            await delay(READ_RETRY_BACKOFF_MS[attempt]);
            continue;
          }
          if (
            !RETRYABLE_STATUS.has(response.status) ||
            attempt >= READ_RETRIES ||
            controller.signal.aborted
          )
            break;
          void response.body?.cancel().catch(() => undefined);
          await delay(READ_RETRY_BACKOFF_MS[attempt]);
        }
        // Do not wait for an error body to report a failed read. Cancel it when
        // available so a streaming error cannot keep the connection occupied.
        if (!response.ok) {
          void response.body?.cancel().catch(() => undefined);
          return { ok: false, status: response.status, body: undefined };
        }
        // A node/proxy that ignores the height cannot authorize an automated
        // movement. Never silently substitute an unpinned latest-state read.
        if (
          blockHeight !== undefined &&
          response.headers.get("x-cosmos-block-height") !==
            blockHeight.toString()
        ) {
          void response.body?.cancel().catch(() => undefined);
          throw new Error("Private balance snapshot height was not honored");
        }
        const body: unknown = await response.json();
        return { ok: true, status: response.status, body };
      })(),
    ]);
  } finally {
    clearTimeout(timer);
  }
}

const TRANSIENT_READ_STATUS = new Set([429, 500, 502, 503, 504]);

/** A read that failed for a passing reason: rate limit, gateway, timeout, network. */
export const isTransientReadError = (error: unknown): boolean =>
  (error instanceof PrivateReadHttpError &&
    TRANSIENT_READ_STATUS.has(error.status)) ||
  error instanceof PrivateReadTimeoutError ||
  // fetch reports a lost connection as a TypeError ("Failed to fetch",
  // "NetworkError…", Safari's "Load failed"); other TypeErrors are bugs.
  (error instanceof TypeError &&
    /fetch|network|load failed/i.test(error.message));

/**
 * Runs a read again after a short pause when it failed for a passing reason
 * (the public LCD rate-limits bursts). Other failures are thrown at once.
 */
export const retryTransientRead = async <T>(
  read: () => Promise<T>,
  delays: readonly number[] = [700, 2_000],
  wait: (ms: number) => Promise<void> = (ms) =>
    new Promise((resolve) => setTimeout(resolve, ms))
): Promise<T> => {
  for (let attempt = 0; ; attempt += 1) {
    try {
      return await read();
    } catch (error) {
      if (attempt >= delays.length || !isTransientReadError(error)) throw error;
      await wait(delays[attempt]);
    }
  }
};
